import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchVisits } from "../../store/slices/visitSlice";
import { fetchTestOrders } from "../../store/slices/testOrderSlice";
import { Activity, ClipboardList, Loader2, RefreshCw, TestTube2, Users } from "lucide-react";

const ReceptionistDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Visits slice
  const {
    visits,
    loading: visitsLoading,
    error: visitsError,
  } = useSelector(
    (s) => s.visits || { visits: [], loading: false, error: null }
  );
  const visitsList = useMemo(
    () => (Array.isArray(visits) ? visits : visits?.data || []),
    [visits]
  );

  // Test orders slice
  const {
    testOrders,
    loading: ordersLoading,
    error: ordersError,
  } = useSelector(
    (s) => s.testOrders || { testOrders: [], loading: false, error: null }
  );
  const ordersList = useMemo(
    () => (Array.isArray(testOrders) ? testOrders : testOrders?.data || []),
    [testOrders]
  );

  // Fetch today's visits and all test orders on mount
  useEffect(() => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const today = `${yyyy}-${mm}-${dd}`;

    dispatch(fetchVisits({ date: today })); // server-side filter for today if supported
    dispatch(fetchTestOrders({})); // add params if backend supports filtering
  }, [dispatch]);

  // Stats
  const todayPatients = useMemo(() => {
    const set = new Set((visitsList || []).map((v) => v.patient_id));
    return set.size;
  }, [visitsList]);

  const pendingTests = useMemo(
    () =>
      (ordersList || []).filter((o) =>
        ["ordered", "sample_collected", "in_progress"].includes(o.status)
      ).length,
    [ordersList]
  );

  const resultsReady = useMemo(
    () => (ordersList || []).filter((o) => o.status === "completed").length,
    [ordersList]
  );

  // Recent patients from today's visits (unique, latest first)
  const recentPatients = useMemo(() => {
    const byPatient = new Map();
    (visitsList || [])
      .slice()
      .sort((a, b) => String(b.visit_time).localeCompare(String(a.visit_time)))
      .forEach((v) => {
        if (!byPatient.has(v.patient_id)) byPatient.set(v.patient_id, v);
      });
    return Array.from(byPatient.values()).slice(0, 5);
  }, [visitsList]);

  const formatTime = (t) => (!t ? "-" : String(t).slice(0, 5));

  const handleViewPatient = (v) => {
    if (v.patient_id) {
      navigate(`/receptionist/patient-details/${v.patient_id}`, {
        state: { from: "dashboard" },
      });
    }
  };

  const isLoading = visitsLoading || ordersLoading;

  return (
    <div className="pt-6">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="rounded-2xl bg-white shadow-sm ring-1 ring-gray-200/70">
          <div className="border-t-4 border-[#36F1A2] bg-gradient-to-r from-gray-50 to-white px-5 py-4 sm:px-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-600">Reception</div>
                <h1 className="mt-1 text-xl font-semibold text-gray-900">Dashboard</h1>
                <p className="mt-1 text-sm text-gray-600">Today’s snapshot and quick actions.</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  const d = new Date();
                  const yyyy = d.getFullYear();
                  const mm = String(d.getMonth() + 1).padStart(2, "0");
                  const dd = String(d.getDate()).padStart(2, "0");
                  dispatch(fetchVisits({ date: `${yyyy}-${mm}-${dd}` }));
                  dispatch(fetchTestOrders({}));
                }}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#235F72] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-[#36F1A2]"
              >
                {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                {isLoading ? "Refreshing…" : "Refresh"}
              </button>
            </div>
          </div>

          <div className="space-y-6 px-5 py-5 sm:px-6">
            {(visitsError || ordersError) ? (
              <div className="rounded-2xl bg-red-50 p-4 text-sm text-red-700 ring-1 ring-red-200">
                {visitsError ? String(visitsError) : null}
                {ordersError ? ` | ${String(ordersError)}` : null}
              </div>
            ) : null}

            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div className="rounded-2xl bg-gray-50 p-4 ring-1 ring-gray-200/70">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-600">Today patients</div>
                    <div className="mt-2 text-2xl font-bold text-[#235F72]">{isLoading ? "—" : todayPatients}</div>
                  </div>
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white ring-1 ring-gray-200">
                    <Users className="h-5 w-5 text-[#235F72]" />
                  </div>
                </div>
              </div>

              <div className="rounded-2xl bg-gray-50 p-4 ring-1 ring-gray-200/70">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-600">Pending tests</div>
                    <div className="mt-2 text-2xl font-bold text-[#235F72]">{isLoading ? "—" : pendingTests}</div>
                  </div>
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white ring-1 ring-gray-200">
                    <Activity className="h-5 w-5 text-[#085DB6]" />
                  </div>
                </div>
              </div>

              <div className="rounded-2xl bg-gray-50 p-4 ring-1 ring-gray-200/70">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-600">Results ready</div>
                    <div className="mt-2 text-2xl font-bold text-[#235F72]">{isLoading ? "—" : resultsReady}</div>
                  </div>
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white ring-1 ring-gray-200">
                    <TestTube2 className="h-5 w-5 text-[#235F72]" />
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-gray-200/70">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold text-gray-900">Quick actions</div>
                  <div className="mt-0.5 text-xs text-gray-600">Jump to common tasks.</div>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <button
                  type="button"
                  onClick={() => navigate("/receptionist/register-patient")}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#36F1A2] px-4 py-3 text-sm font-semibold text-[#235F72] shadow-sm transition hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-[#36F1A2]"
                >
                  <Users className="h-4 w-4" />
                  Register patient
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/receptionist/test-orders")}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#085DB6] px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-[#36F1A2]"
                >
                  <ClipboardList className="h-4 w-4" />
                  Order tests
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/receptionist/visits")}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#235F72] px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-[#36F1A2]"
                >
                  <Activity className="h-4 w-4" />
                  Today’s visits
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/receptionist/test-order-list")}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-4 py-3 text-sm font-semibold text-gray-700 ring-1 ring-gray-200 shadow-sm transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#36F1A2]"
                >
                  <TestTube2 className="h-4 w-4" />
                  Test orders
                </button>
              </div>
            </div>

            <div className="overflow-hidden rounded-2xl bg-white ring-1 ring-gray-200/70">
              <div className="border-b border-gray-100 px-4 py-3">
                <div className="text-sm font-semibold text-gray-900">Recent patients</div>
                <div className="mt-0.5 text-xs text-gray-600">Latest check-ins from today’s visits.</div>
              </div>

              {isLoading ? (
                <div className="flex items-center justify-center gap-2 px-4 py-12 text-sm text-gray-600">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading…
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-600">
                        <th className="px-4 py-3">Name</th>
                        <th className="px-4 py-3">Card</th>
                        <th className="px-4 py-3">Time</th>
                        <th className="px-4 py-3">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {recentPatients.map((v) => {
                        const name =
                          v.patient_name ||
                          v.full_name ||
                          `Patient #${v.patient_id}`;
                        const card = v.card_number || "-";
                        return (
                          <tr key={`${v.id}-${v.patient_id}`} className="hover:bg-gray-50">
                            <td className="px-4 py-3 font-semibold text-gray-900">{name}</td>
                            <td className="px-4 py-3 text-gray-700">{card}</td>
                            <td className="px-4 py-3 text-gray-700">{formatTime(v.visit_time)}</td>
                            <td className="px-4 py-3">
                              <button
                                type="button"
                                onClick={() => handleViewPatient(v)}
                                className="rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-[#085DB6] ring-1 ring-gray-200 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#36F1A2]"
                              >
                                View
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                      {recentPatients.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="px-4 py-12 text-center text-sm text-gray-600">
                            No patients yet today.
                          </td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReceptionistDashboard;
